import type { ReactNode } from 'react'
import { Button, Result } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAuth, usePermission } from '@/hooks'

/**
 * ホスト名ガード。
 *
 * SYSTEM_ADMIN は無条件で通過。それ以外のロールは、ログインユーザの所属自治体に
 * 設定された subdomain が `window.location.hostname` の先頭セグメントと一致しない場合、
 * 403 Result を返してアクセスを拒否する。
 *
 * subdomain が NULL の自治体（移行前の既存行）と、localhost / IP リテラル（ローカル開発）は
 * 制限対象外。誤って締め出された場合に備え、ログアウトボタンを Result に出して別アカウントで
 * 入り直せるようにする。
 */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1'])

export default function SubdomainGuard({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { session, logout } = useAuth()
  const { isSystemAdmin } = usePermission()

  if (isSystemAdmin) {
    return <>{children}</>
  }

  const subdomain = session?.municipalitySubdomain?.trim().toLowerCase() ?? ''
  if (!subdomain) {
    return <>{children}</>
  }

  const host = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase()
  if (LOCAL_HOSTS.has(host)) {
    return <>{children}</>
  }

  const allowed = host === subdomain || host.startsWith(`${subdomain}.`)
  if (!allowed) {
    return (
      <Result
        status="403"
        title={t('wrongSubdomain')}
        subTitle={t('wrongSubdomainDesc')}
        extra={
          <Button type="primary" onClick={() => void logout()}>
            {t('logout')}
          </Button>
        }
      />
    )
  }

  return <>{children}</>
}
